import { FC } from 'react'
import { ArrowRight, Package, BarChart3, Barcode } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { useNavigate } from 'react-router-dom'

export const HeroSection: FC = () => {
  const navigate = useNavigate()

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Ambient glow effect */}
      <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      
      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-20">
            
            {/* Text Content - Simplified, scannable */}
            <div className="flex-1 text-center lg:text-left space-y-8">
              {/* Badge - creates intrigue */}
              <div className="opacity-0 animate-fade-up inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sumoPrimary/10 dark:bg-sumoPrimary/5 border border-sumoPrimary/20">
                <span className="w-2 h-2 rounded-full bg-success500 animate-pulse-soft" />
                <span className="text-sm font-medium text-neutral700 dark:text-neutral300">
                  Built for resellers & small businesses
                </span>
              </div>

              {/* Headline - Strong visual hook, minimal text */}
              <h1 className="opacity-0 animate-fade-up-delay-1 text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-neutral900 dark:text-neutral100 tracking-tight leading-[1.1]">
                Inventory
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sumoPrimary via-sumoAmber to-sumoOrange bg-gradient-animate">
                  Simplified
                </span>
              </h1>

              {/* Subheadline - One clear value proposition */}
              <p className="opacity-0 animate-fade-up-delay-2 text-lg md:text-xl text-neutral600 dark:text-neutral400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Track inventory, manage listings, and grow your business—all from one powerful platform.
              </p>

              {/* Single primary CTA - Reduces decision paralysis */}
              <div className="opacity-0 animate-fade-up-delay-3 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/create-account')}
                  className="group btn-hover-lift btn-glow text-base px-8 py-6 rounded-xl shadow-yellow hover:shadow-strong"
                >
                  Start Free
                  <ArrowRight 
                    className="ml-2 transition-transform duration-200 group-hover:translate-x-1" 
                    width={20} 
                    height={20} 
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="text-base px-8 py-6 rounded-xl text-neutral600 dark:text-neutral400 hover:text-neutral900 dark:hover:text-neutral100"
                >
                  Sign In
                </Button>
              </div>
            </div>

            {/* Visual Hook - Premium dashboard preview */}
            <div className="flex-1 w-full max-w-lg lg:max-w-xl opacity-0 animate-fade-up-delay-2">
              <div className="relative">
                {/* Floating glow behind card */}
                <div className="absolute -inset-4 bg-gradient-to-br from-sumoPrimary/20 via-sumoAmber/10 to-sumoOrange/20 dark:from-sumoPrimary/10 dark:via-sumoAmber/5 dark:to-sumoOrange/10 rounded-3xl blur-2xl" />
                
                {/* Main dashboard card */}
                <div className="relative bg-white dark:bg-neutral800 rounded-2xl p-6 md:p-8 shadow-strong border border-neutral200 dark:border-neutral700 animate-float">
                  
                  {/* Dashboard header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sumoPrimary to-sumoAmber flex items-center justify-center">
                        <Package className="w-5 h-5 text-sumoNavy" />
                      </div>
                      <div>
                        <div className="font-semibold text-neutral900 dark:text-neutral100">Dashboard</div>
                        <div className="text-xs text-neutral500">Today's overview</div>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-error500/60" />
                      <div className="w-3 h-3 rounded-full bg-sumoAmber/60" />
                      <div className="w-3 h-3 rounded-full bg-success500/60" />
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-3 rounded-xl bg-neutral50 dark:bg-neutral700/50">
                      <div className="text-2xl font-bold text-neutral900 dark:text-neutral100">847</div>
                      <div className="text-xs text-neutral500">Items</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-success50 dark:bg-success500/10">
                      <div className="text-2xl font-bold text-success700 dark:text-success500">$2.4k</div>
                      <div className="text-xs text-neutral500">Revenue</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-sumoPrimary/10 dark:bg-sumoPrimary/10">
                      <div className="text-2xl font-bold text-sumoOrange">23</div>
                      <div className="text-xs text-neutral500">Active</div>
                    </div>
                  </div>

                  {/* Mini chart visualization */}
                  <div className="flex items-end gap-1.5 h-16 mb-4">
                    {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((height, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-sumoPrimary to-sumoAmber opacity-80"
                        style={{ height: `${height}%` }}
                      />
                    ))}
                  </div>

                  {/* Feature icons */}
                  <div className="flex justify-center gap-6 pt-4 border-t border-neutral200 dark:border-neutral700">
                    <div className="flex items-center gap-2 text-sm text-neutral600 dark:text-neutral400">
                      <BarChart3 className="w-4 h-4 text-sumoTeal" />
                      <span>Analytics</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral600 dark:text-neutral400">
                      <Barcode className="w-4 h-4 text-sumoOrange" />
                      <span>Barcodes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
